import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../axiosInstance";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Divider,
  Rating,
  TextField,
  Button,
  Popover,
  IconButton,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import StarIcon from "@mui/icons-material/Star";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
);

const cardStyle = {
  borderRadius: 3,
  background: "rgba(255,255,255,0.78)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255,255,255,0.6)",
};

const formatPercentTrend = (pct) => {
  if (pct == null) return "No change data";
  const rounded = Math.abs(Number(pct)).toFixed(1);
  return `${pct >= 0 ? "↑" : "↓"} ${rounded}%`;
};

const formatINR = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value ?? 0));

const buildImageUrl = (path) =>
  path
    ? `https://siaedutech.com/public${path.startsWith("/") ? "" : "/"}${path}`
    : null;

const buildStats = (s = {}) => [
  {
    icon: <CalendarMonthIcon sx={{ fontSize: 32, color: "#42a5f5" }} />,
    iconBg: "linear-gradient(135deg, #f0f7ff, #bbdefb)",
    borderColor: "#64b5f6",
    label: "Total Sessions",
    value: `${s.totalSessions ?? 0}`,
    trend: formatPercentTrend(s.totalSessionsGrowthPercentage),
    trendColor: "#42a5f5",
    trendUp: (s.totalSessionsGrowthPercentage ?? 0) >= 0,
    glowColor: "rgba(100,181,246,0.18)",
  },
  {
    icon: <CheckCircleIcon sx={{ fontSize: 32, color: "#4caf50" }} />,
    iconBg: "linear-gradient(135deg, #f1f8f1, #dcedc8)",
    borderColor: "#81c784",
    label: "Completed",
    value: `${s.completedSessions ?? 0}`,
    trend: formatPercentTrend(s.completedSessionsGrowthPercentage),
    trendColor: "#66bb6a",
    trendUp: (s.completedSessionsGrowthPercentage ?? 0) >= 0,
    glowColor: "rgba(129,199,132,0.18)",
  },
  {
    icon: <MonetizationOnIcon sx={{ fontSize: 32, color: "#ffb74d" }} />,
    iconBg: "linear-gradient(135deg, #fffdf0, #fff3cd)",
    borderColor: "#ffd54f",
    label: "Total Earnings",
    value: formatINR(s.totalEarnings),
    trend: formatPercentTrend(s.totalEarningsGrowthPercentage),
    trendColor: "#ffa726",
    trendUp: (s.totalEarningsGrowthPercentage ?? 0) >= 0,
    glowColor: "rgba(255,213,79,0.18)",
  },
  {
    icon: <StarIcon sx={{ fontSize: 32, color: "#ff8a65" }} />,
    iconBg: "linear-gradient(135deg, #fff5f2, #ffd5c8)",
    borderColor: "#ffab91",
    label: "Avg Rating",
    value: `${s.averageRating ?? 0}`,
    trend: `from ${s.ratingCount ?? 0} reviews`,
    trendColor: "#ff8a65",
    trendUp: true,
    glowColor: "rgba(255,171,145,0.18)",
  },
  {
    icon: <AutorenewIcon sx={{ fontSize: 32, color: "#ff9800" }} />,
    iconBg: "linear-gradient(135deg, #fff8f0, #ffe0b2)",
    borderColor: "#ffb74d",
    label: "Rescheduled",
    value: `${s.rescheduledSessions ?? 0}`,
    trend: s.totalSessions
      ? `${Math.round(((s.rescheduledSessions ?? 0) / s.totalSessions) * 100)}% of sessions`
      : "of sessions",
    trendColor: "#ff9800",
    trendUp: true,
    glowColor: "rgba(255,183,77,0.18)",
  },
  {
    icon: <CancelIcon sx={{ fontSize: 32, color: "#ef5350" }} />,
    iconBg: "linear-gradient(135deg, #fff2f2, #ffcdd2)",
    borderColor: "#ef9a9a",
    label: "Cancelled",
    value: `${s.cancelledSessions ?? 0}`,
    trend: s.totalSessions
      ? `${Math.round(((s.cancelledSessions ?? 0) / s.totalSessions) * 100)}% of sessions`
      : "of sessions",
    trendColor: "#ef5350",
    trendUp: false,
    glowColor: "rgba(239,154,154,0.18)",
  },
  {
    icon: <EventAvailableIcon sx={{ fontSize: 32, color: "#26c6da" }} />,
    iconBg: "linear-gradient(135deg, #e0f7fa, #b2ebf2)",
    borderColor: "#80deea",
    label: "Upcoming",
    value: `${s.upcomingSessions ?? 0}`,
    trend: "Scheduled ahead",
    trendColor: "#26c6da",
    trendUp: true,
    glowColor: "rgba(128,222,234,0.18)",
  },
  {
    icon: <AccessTimeIcon sx={{ fontSize: 32, color: "#5c6bc0" }} />,
    iconBg: "linear-gradient(135deg, #f0f1fc, #c5cae9)",
    borderColor: "#9fa8da",
    label: "Total Availability",
    value: `${s.totalAvailability ?? 0} hrs`,
    trend: "Hours available",
    trendColor: "#5c6bc0",
    trendUp: true,
    glowColor: "rgba(159,168,218,0.18)",
  },
];

// Picks a "nice" round step (multiple of 1000) so consecutive ticks never
// round to the same ₹Xk label on the y-axis.
const getNiceThousandStep = (maxValue) => {
  if (!maxValue || maxValue <= 0) return 1000;
  const roughStep = maxValue / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const residual = roughStep / magnitude;
  let niceResidual = 1;
  if (residual > 5) niceResidual = 10;
  else if (residual > 2) niceResidual = 5;
  else if (residual > 1) niceResidual = 2;
  return Math.max(1000, Math.round((niceResidual * magnitude) / 1000) * 1000);
};

const buildEarningsAreaData = (revenueTrend = []) => ({
  labels: revenueTrend.map((d) => d.date),
  datasets: [
    {
      label: "Earnings (₹)",
      fill: true,
      data: revenueTrend.map((d) => d.revenue),
      backgroundColor: (ctx) => {
        const { ctx: c, chartArea } = ctx.chart;
        if (!chartArea) return "rgba(66,165,245,0.1)";
        const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        g.addColorStop(0, "rgba(66,165,245,0.35)");
        g.addColorStop(1, "rgba(66,165,245,0.02)");
        return g;
      },
      borderColor: "#42a5f5",
      borderWidth: 2.5,
      pointBackgroundColor: "#42a5f5",
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0.4,
    },
  ],
});

const buildSessionTypeDoughnut = (d = {}) => ({
  labels: ["Completed", "Upcoming", "Cancelled"],
  datasets: [
    {
      data: [
        d.completedPercentage ?? 0,
        d.upcomingPercentage ?? 0,
        d.cancelledPercentage ?? 0,
      ],
      backgroundColor: ["#81c784", "#64b5f6", "#ff984f"],
      borderColor: ["#fff", "#fff", "#fff"],
      borderWidth: 3,
      hoverOffset: 8,
    },
  ],
});

const chartOptions = (yLabel, stepSize) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "rgba(255,255,255,0.95)",
      titleColor: "#1a1a2e",
      bodyColor: "#555",
      borderColor: "#e0e0e0",
      borderWidth: 1,
      padding: 10,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#9e9e9e", font: { size: 11 } },
    },
    y: {
      beginAtZero: true,
      grid: { color: "rgba(0,0,0,0.04)" },
      ticks: { color: "#9e9e9e", font: { size: 11 }, stepSize, callback: yLabel },
    },
  },
});

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "50%",
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        padding: 14,
        font: { size: 12 },
        color: "#555",
        usePointStyle: true,
      },
    },
    tooltip: {
      backgroundColor: "rgba(255,255,255,0.95)",
      titleColor: "#1a1a2e",
      bodyColor: "#555",
      borderColor: "#e0e0e0",
      borderWidth: 1,
      padding: 10,
      callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.parsed}%` },
    },
  },
};

const REVIEWS_PREVIEW_COUNT = 6;

const formatReviewDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

function ReviewCard({ review }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        background: "rgba(255,171,145,0.06)",
        border: "1px solid rgba(255,171,145,0.2)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="body2" fontWeight={700} color="#1a1a2e">
          {review.studentName || "Anonymous"}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {formatReviewDate(review.reviewDate)}
          </Typography>
          <Rating
            value={review.rating || 0}
            readOnly
            size="small"
            sx={{ mb: 0.8 }}
          />
        </Box>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontStyle: "italic" }}
      >
        "{review.review}"
      </Typography>
    </Box>
  );
}

function MentorAnalytics() {
  const location = useLocation();
  const navigate = useNavigate();
  const { mentorData } = location.state ?? {};
  const mentorId = mentorData?.mentorId;
  const [activeFilter, setActiveFilter] = useState("1M");
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [mentorCharts, setMentorCharts] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showAllReviews, setShowAllReviews] = useState(false);

  const filters = ["1M", "3M", "6M", "1Y"];

  const getDateRange = (filter, customStart, customEnd) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split("T")[0];
    if (filter === "custom") return { start: customStart, end: customEnd };
    const end = fmt(today);
    const start = new Date(today);
    if (filter === "1M") start.setMonth(start.getMonth() - 1);
    else if (filter === "3M") start.setMonth(start.getMonth() - 3);
    else if (filter === "6M") start.setMonth(start.getMonth() - 6);
    else start.setFullYear(start.getFullYear() - 1);
    return { start: fmt(start), end };
  };

  const { start: resolvedStart, end: resolvedEnd } = getDateRange(
    activeFilter,
    startDate,
    endDate,
  );

  useEffect(() => {
    if (!mentorId) return;
    if (activeFilter === "custom" && (!resolvedStart || !resolvedEnd)) return;
    const params = { startDate: resolvedStart, endDate: resolvedEnd };
    api
      .get(`/admin/mentors/${mentorId}/analytics`, { params })
      .then((res) => setAnalytics(res.data?.data || null))
      .catch((err) => console.error("Mentor analytics fetch error:", err));
    api
      .get(`/admin/mentors/${mentorId}/charts`, { params })
      .then((res) => setMentorCharts(res.data?.data || null))
      .catch((err) => console.error("Mentor charts fetch error:", err));
    api
      .get(`/admin/mentors/${mentorId}/reviews`, { params })
      .then((res) => setReviews(res.data?.data || []))
      .catch((err) => console.error("Mentor reviews fetch error:", err));
  }, [mentorId, resolvedStart, resolvedEnd, activeFilter]);

  const info = analytics?.mentorInformation || {};
  const statistics = analytics?.statistics || {};
  const stats = buildStats(statistics);
  const revenueTrend = mentorCharts?.revenueTrend?.data || [];
  const bookingStatusDistribution =
    mentorCharts?.bookingStatusDistribution || {};
  const earningsAreaData = buildEarningsAreaData(revenueTrend);
  const earningsMax = Math.max(0, ...revenueTrend.map((d) => d.revenue ?? 0));
  const earningsStep = getNiceThousandStep(earningsMax);
  const sessionTypeDoughnut = buildSessionTypeDoughnut(
    bookingStatusDistribution,
  );

  const taughtByCategory = (info.taughtSubjects || []).reduce((acc, subj) => {
    const key = subj.coreSubjectName || "Other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(subj);
    return acc;
  }, {});

  const handleFilterChange = (val) => {
    setActiveFilter(val);
    setAnchorEl(null);
  };

  const handleCustomApply = () => {
    if (startDate && endDate) {
      setActiveFilter("custom");
      setAnchorEl(null);
    }
  };

  const filterLabel =
    activeFilter === "custom" && startDate && endDate
      ? `${startDate} – ${endDate}`
      : activeFilter === "1M"
        ? "Last 1 Month"
        : activeFilter === "3M"
          ? "Last 3 Months"
          : activeFilter === "6M"
            ? "Last 6 Months"
            : "Last 1 Year";

  return (
    <Box sx={{ p: 2 }}>
      {/* Breadcrumb */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Breadcrumbs separator="›" sx={{ fontSize: "0.82rem" }}>
          <Link
            underline="hover"
            color="inherit"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.4,
              cursor: "pointer",
              fontSize: "0.82rem",
            }}
            onClick={() => navigate("/mentorship")}
          >
            <HomeIcon sx={{ fontSize: 15 }} /> Mentorship
          </Link>
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: "pointer", fontSize: "0.82rem" }}
            onClick={() => navigate(-1)}
          >
            Admin
          </Link>
          <Typography
            color="text.primary"
            sx={{ fontSize: "0.82rem", fontWeight: 600 }}
          >
            {mentorData?.mentorName ?? "Analytics"}
          </Typography>
        </Breadcrumbs>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          size="small"
          sx={{ textTransform: "none", fontWeight: 600, color: "#667eea" }}
        >
          Back
        </Button>
      </Box>
      {/* Filter Bar */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2.5 }}>
        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            cursor: "pointer",
            background: "rgba(255,255,255,0.85)",
            border: "1px solid #e8eaf0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            backdropFilter: "blur(8px)",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              borderColor: "#bdbdbd",
            },
          }}
        >
          <CalendarTodayIcon sx={{ fontSize: 16, color: "#667eea" }} />
          <Typography
            sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#444" }}
          >
            {filterLabel}
          </Typography>
          <Box sx={{ fontSize: 10, color: "#aaa", ml: 0.5 }}>▼</Box>
        </Box>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              boxShadow: "0 12px 40px rgba(0,0,0,0.13)",
              minWidth: 220,
              overflow: "hidden",
              border: "1px solid #f0f0f0",
            },
          }}
        >
          {[
            { label: "Last 1 Month", value: "1M" },
            { label: "Last 3 Months", value: "3M" },
            { label: "Last 6 Months", value: "6M" },
            { label: "Last 1 Year", value: "1Y" },
          ].map((opt) => (
            <Box
              key={opt.value}
              onClick={() => handleFilterChange(opt.value)}
              sx={{
                px: 2.5,
                py: 1.2,
                cursor: "pointer",
                fontSize: "0.83rem",
                fontWeight: activeFilter === opt.value ? 700 : 500,
                color: activeFilter === opt.value ? "#667eea" : "#444",
                background:
                  activeFilter === opt.value
                    ? "rgba(102,126,234,0.07)"
                    : "#fff",
                borderLeft:
                  activeFilter === opt.value
                    ? "3px solid #667eea"
                    : "3px solid transparent",
                transition: "all 0.15s",
                "&:hover": {
                  background: "rgba(102,126,234,0.06)",
                  color: "#667eea",
                },
              }}
            >
              {opt.label}
            </Box>
          ))}
          <Box sx={{ height: "1px", background: "#f0f0f0", mx: 2 }} />
          <Box sx={{ p: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="#aaa"
              sx={{
                textTransform: "uppercase",
                letterSpacing: 1,
                fontSize: "0.68rem",
              }}
            >
              Custom Range
            </Typography>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.2, mt: 1 }}
            >
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "0.82rem",
                  },
                }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "0.82rem",
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setActiveFilter("1M");
                    setAnchorEl(null);
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    borderColor: "#e0e0e0",
                    color: "#888",
                  }}
                >
                  Clear
                </Button>
                <Button
                  size="small"
                  fullWidth
                  variant="contained"
                  onClick={handleCustomApply}
                  disabled={!startDate || !endDate}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    boxShadow: "none",
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>
      </Box>
      {/* Mentor Profile Header */}
      <Card
        sx={{
          ...cardStyle,
          boxShadow: "0 4px 24px rgba(102,126,234,0.12)",
          borderTop: "2px solid #667eea",
          mb: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              flexWrap: "wrap",
            }}
          >
            <Avatar
              src={buildImageUrl(info.profileImage)}
              sx={{
                width: 80,
                height: 80,
                fontSize: "2rem",
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                boxShadow: "0 4px 16px rgba(102,126,234,0.35)",
              }}
            >
              {(info.fullName || "?")
                .split(" ")
                .map((w) => w[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, textAlign: "left" }}>
              <Typography variant="h5" fontWeight={800} color="#1a1a2e">
                {info.fullName || "—"}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.3 }}
              >
                {info.coreSubjectCategories
                  ?.map((c) => c.subjectName)
                  .join(", ") || "—"}
                {info.experience ? ` | ${info.experience} experience` : ""}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                {[info.email, info.mobileNumber].filter(Boolean).join(" · ")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {(info.coreSubjectCategories || []).map((s) => (
                  <Chip
                    key={s.id}
                    label={s.subjectName}
                    size="small"
                    sx={{
                      background: "rgba(102,126,234,0.1)",
                      color: "#667eea",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                    }}
                  />
                ))}
              </Box>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  justifyContent: "flex-end",
                }}
              >
                <Rating
                  value={info.averageRating || 0}
                  precision={0.1}
                  readOnly
                  size="small"
                />
                <Typography variant="body2" fontWeight={700} color="#ff8a65">
                  {info.averageRating ?? 0}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {info.ratingCount ?? 0} reviews
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={
                    info.status === "ACTIVE"
                      ? "Active"
                      : info.status || "Unknown"
                  }
                  size="small"
                  sx={{
                    background:
                      info.status === "ACTIVE"
                        ? "rgba(76,175,80,0.12)"
                        : "rgba(239,83,80,0.12)",
                    color: info.status === "ACTIVE" ? "#4caf50" : "#ef5350",
                    fontWeight: 700,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        {stats.map((stat, i) => (
          <Card
            key={i}
            sx={{
              ...cardStyle,
              minHeight: 128,
              display: "flex",
              flexDirection: "column",
              boxShadow: `0 4px 20px ${stat.glowColor}`,
              borderTop: `3px solid ${stat.borderColor}`,
              transition: "transform 0.2s",
              "&:hover": { transform: "translateY(-3px)" },
            }}
          >
            <CardContent
              sx={{
                p: 2,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                "&:last-child": { pb: 2 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    background: stat.iconBg,
                    borderRadius: 2,
                    width: 48,
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 2px 8px ${stat.glowColor}`,
                    flexShrink: 0,
                  }}
                >
                  {stat.icon}
                </Box>
                <Box sx={{ flex: 1, textAlign: "left" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{
                      fontSize: "0.85rem",
                      display: "block",
                      mb: 0.5,
                      textAlign: "left",
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{
                      fontSize: "1.7rem",
                      color: "#1a1a2e",
                      textAlign: "left",
                    }}
                  >
                    {stat.value}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                {stat.trendUp ? (
                  <TrendingUpIcon
                    sx={{ fontSize: 16, color: stat.trendColor }}
                  />
                ) : (
                  <TrendingDownIcon
                    sx={{ fontSize: 16, color: stat.trendColor }}
                  />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: stat.trendColor,
                    fontWeight: 600,
                    fontSize: "0.8rem",
                  }}
                >
                  {stat.trend}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Charts Row 1 */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.8fr 0.7fr 0.7fr" },
          gap: 2.5,
          mb: 2.5,
        }}
      >
        {/* Monthly Earnings Bar */}
        <Card
          sx={{
            ...cardStyle,
            boxShadow: "0 4px 20px rgba(255,213,79,0.15)",
            borderTop: "4px solid #42a5f5",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700} color="#1a1a2e">
                  Revenue Trend
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {filterLabel}
                </Typography>
              </Box>
              <Chip
                label={formatPercentTrend(
                  statistics.totalEarningsGrowthPercentage,
                )}
                size="small"
                sx={{
                  background: "rgba(79, 196, 255, 0.15)",
                  color: "#42a5f5",
                  fontWeight: 700,
                }}
              />
            </Box>
            {revenueTrend.length ? (
              <Box sx={{ height: 240 }}>
                <Line
                  data={earningsAreaData}
                  options={chartOptions(
                    (v) => `₹${(v / 1000).toFixed(0)}k`,
                    earningsStep,
                  )}
                />
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 6, textAlign: "center" }}
              >
                No data available
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Session Type Breakdown */}
        <Card
          sx={{
            ...cardStyle,
            boxShadow: "0 4px 20px rgba(129,199,132,0.15)",
            borderTop: "4px solid #81c784",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700} color="#1a1a2e">
                  Sessions
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Distribution by type
                </Typography>
              </Box>
              <Box
                sx={{
                  background: "linear-gradient(135deg, #f0f7ff, #bbdefb)",
                  borderRadius: 2,
                  px: 1.2,
                  py: 0.4,
                  border: "1px solid #64b5f6",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    lineHeight: 1.2,
                    fontSize: "0.65rem",
                  }}
                >
                  Rescheduled
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ color: "#1565c0" }}
                >
                  {bookingStatusDistribution.rescheduledCount ?? 0}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ height: 240 }}>
              <Doughnut data={sessionTypeDoughnut} options={doughnutOptions} />
            </Box>
          </CardContent>
        </Card>
        {/* Skills */}
        <Card
          sx={{
            ...cardStyle,
            height: 420, // Fixed height
            boxShadow: "0 4px 20px rgba(206,147,216,0.15)",
            borderTop: "4px solid #ce93d8",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              "&:last-child": {
                pb: 3,
              },
            }}
          >
            <Box sx={{ mb: 1.5, textAlign: "left", flexShrink: 0 }}>
              <Typography variant="h6" fontWeight={700} color="#1a1a2e">
                Subjects Taught
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                textAlign: "left",
                pr: 0.5,

                "&::-webkit-scrollbar": {
                  width: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#ce93d8",
                  borderRadius: 3,
                },
                "&::-webkit-scrollbar-track": {
                  background: "rgba(206,147,216,0.08)",
                },
              }}
            >
              {Object.keys(taughtByCategory).length ? (
                Object.entries(taughtByCategory).map(([category, subjects]) => (
                  <Box
                    key={category}
                    sx={{
                      borderRadius: 2,
                      border: "1px solid rgba(206,147,216,0.25)",
                      background: "rgba(206,147,216,0.05)",
                      overflow: "hidden",
                      mb: 1.2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 1.5,
                        py: 0.8,
                        background: "rgba(206,147,216,0.12)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="#8e24aa"
                        sx={{ fontSize: "0.78rem" }}
                      >
                        {category}
                      </Typography>

                      <Chip
                        label={subjects.length}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          background: "#ce93d8",
                          color: "#fff",
                        }}
                      />
                    </Box>

                    <Box sx={{ px: 1.5, py: 0.8 }}>
                      {subjects.map((subj, idx) => (
                        <Box
                          key={subj.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            py: 0.6,
                            borderTop:
                              idx === 0
                                ? "none"
                                : "1px solid rgba(206,147,216,0.15)",
                          }}
                        >
                          <Box
                            sx={{
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              background: "#ce93d8",
                              flexShrink: 0,
                            }}
                          />

                          <Typography
                            variant="caption"
                            color="#444"
                            sx={{
                              fontSize: "0.76rem",
                              lineHeight: 1.3,
                            }}
                          >
                            {subj.subjectName}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No subjects available
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Charts Row 2 */}

      {/* Reviews */}
      <Card
        sx={{
          ...cardStyle,
          boxShadow: "0 4px 20px rgba(255,171,145,0.15)",
          borderTop: "4px solid #ffab91",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight={700} color="#1a1a2e">
              Recent Reviews
            </Typography>
            {reviews.length > REVIEWS_PREVIEW_COUNT && (
              <Typography
                onClick={() => setShowAllReviews(true)}
                sx={{
                  cursor: "pointer",
                  color: "#ff8a65",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                View All ({reviews.length})
              </Typography>
            )}
          </Box>
          {reviews.length ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              {reviews.slice(0, REVIEWS_PREVIEW_COUNT).map((r, i) => (
                <ReviewCard key={i} review={r} />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No reviews available
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* All Reviews Dialog */}
      <Dialog
        open={showAllReviews}
        onClose={() => setShowAllReviews(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 700,
          }}
        >
          All Reviews ({reviews.length})
          <IconButton size="small" onClick={() => setShowAllReviews(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: 2,
            }}
          >
            {reviews.map((r, i) => (
              <ReviewCard key={i} review={r} />
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default MentorAnalytics;
